import type { User } from '../../auth/types';
import { ROUTES, resolveRoutePolicy } from '../../navigation/routePolicy';
import type { RouterBasename } from '../../navigation/types';
import { canAccessStaffRoute } from '../../features/summer-training/auth/roleAccess';

export interface CrmView { id: string; path: string; en: string; ar: string }

/** Mirrors the existing RoutedAppShell gates; changing perspective never changes identity. */
export function getCrmViews(user: User, summer = false): CrmView[] {
  const role = String(user.role || '').toLowerCase();
  const gm = role === 'general_manager';
  const manager = role.includes('manager') && role !== 'sales_manager';
  const views: CrmView[] = [];
  const add = (allowed: boolean, id: string, path: string, en: string, ar: string) => {
    if (allowed) views.push({ id, path, en, ar });
  };
  if (summer) {
    const sections = [
      ['operations', 'Operations View', 'منظور العمليات'], ['finance', 'Finance View', 'منظور الحسابات'],
      ['mentor', 'Mentor View', 'منظور المشرف'], ['lecturer', 'Lecturer View', 'منظور المحاضر'],
      ['admin', 'Admin View', 'منظور الإدارة'], ['reports', 'Reports View', 'منظور التقارير'],
    ];
    for (const [id, en, ar] of sections) add(canAccessStaffRoute(role, id), id, `/summer-training/${id}`, en, ar);
    return views;
  }
  add(manager, 'manager', '/manager', gm ? 'General Manager View' : 'Operations Manager View', gm ? 'منظور المدير العام' : 'منظور مدير العمليات');
  add(role.includes('reception') || manager, 'reception', '/reception/students', 'Reception View', 'منظور الاستقبال');
  add(['sales_manager', 'team_leader', 'sales', 'agent', 'tele_sales', 'ghosted_sales'].includes(role) || gm,
    'sales', '/sales', role === 'sales_manager' || gm ? 'Sales Manager View' : role === 'team_leader' ? 'Team Leader View' : role === 'tele_sales' ? 'Tele-Sales View' : 'Sales View',
    role === 'sales_manager' || gm ? 'منظور مدير المبيعات' : role === 'team_leader' ? 'منظور قائد الفريق' : role === 'tele_sales' ? 'منظور المبيعات الهاتفية' : 'منظور المبيعات');
  add(role.includes('hr') || gm, 'hr', '/hr', 'HR View', 'منظور الموارد البشرية');
  add(role === 'accountant' || gm, 'accountant', '/accountant/dashboard', 'Finance View', 'منظور الحسابات');
  add(role === 'marketing' || gm, 'marketing', '/marketing/dashboard', 'Marketing View', 'منظور التسويق');
  add(role.includes('instructor'), 'instructor', '/instructor', 'Instructor View', 'منظور المحاضر');
  add(role === 'developer' || Boolean(user.is_developer), 'developer', '/developer/cockpit', 'Developer View', 'منظور المطور');
  return views;
}

export function isCrmHeaderRoute(basename: RouterBasename, pathname: string, user: User | null): boolean {
  if (!user || user.role === 'student') return false;
  const policy = resolveRoutePolicy({ basename, pathname });
  if (!policy || policy.guard === 'public' || policy.shell === 'student' || policy.guard === 'summer-student') return false;
  // Receipts are intentionally independent printable documents.
  return !/receipt|invoice-receipt|invoice-print/.test(policy.id)
    && policy.id !== ROUTES['reception.invoiceReceipt'].id;
}
